// Iter-207bf — Manual CRA Reminder (multi-select bulk version).
//
// UX: Pick week or month → tick the checkboxes of any collaborator(s) (no
// constraint on submission status — admin is in charge), choose channels
// once globally, hit "Relancer N collaborateur(s)" → response per-user with
// status (sent / skipped_lock / user_not_found / all_failed).
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Loader2, BellRing, Mail, CheckCircle2, AlertCircle, Calendar,
  CheckSquare, Square, Send, Filter,
} from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const MONTH_FR = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
                  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

function getISOWeek(d) {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return {
    year: date.getUTCFullYear(),
    iso_week: Math.ceil(((date - yearStart) / 86400000 + 1) / 7),
  };
}

export default function CraReminderManual() {
  const today = new Date();
  const currentIso = getISOWeek(today);
  const prevMonthDate = new Date(today.getFullYear(), today.getMonth() - 1, 15);

  const [mode, setMode] = useState("weekly");
  const [year, setYear] = useState(currentIso.year);
  const [isoWeek, setIsoWeek] = useState(currentIso.iso_week);
  const [monthlyYear, setMonthlyYear] = useState(prevMonthDate.getFullYear());
  const [month, setMonth] = useState(prevMonthDate.getMonth() + 1);

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-5" data-testid="reminder-manual-page">
      <header className="space-y-1">
        <h1 className="text-2xl font-light tracking-tight" data-testid="reminder-manual-title">
          Manual CRA Reminder
        </h1>
        <p className="text-sm text-zinc-500 leading-relaxed">
          Cochez un ou plusieurs collaborateurs et envoyez-leur une relance pour
          la semaine ou le mois sélectionné. Pour les règles automatiques, voir{" "}
          <Link to="/admin/cra-reminder-setup" className="text-blue-700 hover:underline" data-testid="reminder-manual-link-setup">
            Reminder Setup
          </Link>.
        </p>
      </header>

      <div
        className="inline-flex rounded-md border border-zinc-200 overflow-hidden text-[11px] font-mono uppercase tracking-wider"
        data-testid="reminder-manual-mode-switch"
        role="tablist"
      >
        <button
          onClick={() => setMode("weekly")}
          className={`px-3 h-8 ${mode === "weekly" ? "bg-zinc-900 text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}
          data-testid="reminder-manual-mode-weekly"
          role="tab" aria-selected={mode === "weekly"}
        >
          Weekly CRA
        </button>
        <button
          onClick={() => setMode("monthly")}
          className={`px-3 h-8 border-l border-zinc-200 ${mode === "monthly" ? "bg-zinc-900 text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}
          data-testid="reminder-manual-mode-monthly"
          role="tab" aria-selected={mode === "monthly"}
        >
          Monthly CRA
        </button>
      </div>

      {mode === "weekly" ? (
        <ReminderPicker
          kind="weekly"
          year={year} period={isoWeek}
          setYear={setYear} setPeriod={setIsoWeek}
        />
      ) : (
        <ReminderPicker
          kind="monthly"
          year={monthlyYear} period={month}
          setYear={setMonthlyYear} setPeriod={setMonth}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reusable picker for weekly/monthly bulk reminder
// ---------------------------------------------------------------------------
function ReminderPicker({ kind, year, period, setYear, setPeriod }) {
  const [data, setData] = useState({ users: [] });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [channels, setChannels] = useState(kind === "monthly" ? ["email"] : ["inapp", "email"]);
  const [hideSubmitted, setHideSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = kind === "weekly"
        ? `year=${year}&iso_week=${period}`
        : `year=${year}&month=${period}`;
      const r = await api.get(`/admin/cra-reminder/manual/users?${qs}`);
      setData(r.data);
      setSelected(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [kind, year, period]);

  useEffect(() => { load(); }, [load]);

  const visibleUsers = useMemo(() => {
    if (!hideSubmitted) return data.users;
    return data.users.filter((u) => {
      const sub = kind === "weekly" ? u.weekly?.submitted : u.monthly?.submitted;
      return !sub;
    });
  }, [data.users, hideSubmitted, kind]);

  const allVisibleSelected = visibleUsers.length > 0
    && visibleUsers.every((u) => selected.has(u.user_id));

  const toggleAll = () => {
    setSelected((curr) => {
      const next = new Set(curr);
      if (allVisibleSelected) {
        visibleUsers.forEach((u) => next.delete(u.user_id));
      } else {
        visibleUsers.forEach((u) => next.add(u.user_id));
      }
      return next;
    });
  };
  const toggleOne = (uid) => {
    setSelected((curr) => {
      const next = new Set(curr);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };
  const toggleChannel = (c) => {
    setChannels((curr) => curr.includes(c) ? curr.filter((x) => x !== c) : [...curr, c]);
  };

  const send = async () => {
    if (selected.size === 0) {
      toast.error("Sélectionnez au moins un collaborateur.");
      return;
    }
    if (channels.length === 0) {
      toast.error("Sélectionnez au moins un canal de notification.");
      return;
    }
    const submittedCount = visibleUsers
      .filter((u) => selected.has(u.user_id))
      .filter((u) => kind === "weekly" ? u.weekly?.submitted : u.monthly?.submitted)
      .length;
    if (submittedCount > 0) {
      if (!window.confirm(
        `${submittedCount} collaborateur(s) sélectionné(s) ont déjà soumis leur CRA pour cette période. Voulez-vous quand même leur envoyer la relance ?`
      )) return;
    }
    setSending(true);
    try {
      const url = `/admin/cra-reminder/manual/${kind}`;
      const body = kind === "weekly"
        ? { user_ids: Array.from(selected), year, iso_week: period, channels }
        : { user_ids: Array.from(selected), year, month: period, channels };
      const r = await api.post(url, body);
      const sent = r.data.sent_count;
      const total = r.data.total;
      const locked = (r.data.results || []).filter((x) => x.status === "skipped_lock").length;
      const msg = locked > 0
        ? `${sent}/${total} relances envoyées (${locked} ignorées : relance récente < 30 s)`
        : `${sent}/${total} relances envoyées`;
      if (sent > 0) toast.success(msg);
      else toast.warning(msg);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi");
    } finally {
      setSending(false);
    }
  };

  const periodLabel = kind === "weekly"
    ? `Semaine ${period} · ${year}`
    : `${MONTH_FR[period - 1]} ${year}`;

  return (
    <section className="space-y-3" data-testid={`reminder-manual-${kind}`}>
      {/* Period picker */}
      <div className="flex items-center gap-2 flex-wrap">
        <Calendar size={14} className="text-zinc-600" />
        <span className="text-xs text-zinc-600">Période :</span>
        {kind === "weekly" ? (
          <>
            <input
              type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value || "0", 10))}
              className="w-20 h-7 border border-zinc-200 rounded px-2 text-xs font-mono"
              data-testid={`reminder-manual-${kind}-year`}
            />
            <span className="text-xs text-zinc-500">Semaine</span>
            <input
              type="number" min={1} max={53} value={period}
              onChange={(e) => setPeriod(parseInt(e.target.value || "0", 10))}
              className="w-16 h-7 border border-zinc-200 rounded px-2 text-xs font-mono"
              data-testid={`reminder-manual-${kind}-input`}
            />
          </>
        ) : (
          <>
            <select
              value={period} onChange={(e) => setPeriod(parseInt(e.target.value, 10))}
              className="h-7 border border-zinc-200 rounded px-2 text-xs"
              data-testid={`reminder-manual-${kind}-month`}
            >
              {MONTH_FR.map((label, idx) => (
                <option key={idx} value={idx + 1}>{label}</option>
              ))}
            </select>
            <select
              value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))}
              className="h-7 border border-zinc-200 rounded px-2 text-xs font-mono"
              data-testid={`reminder-manual-${kind}-year`}
            >
              {[year - 1, year, year + 1].map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </>
        )}
        <span className="text-[11px] font-mono text-zinc-400 ml-2">{periodLabel}</span>
      </div>

      {/* Filter + channel selectors */}
      <div className="flex items-center gap-3 flex-wrap p-2.5 border border-zinc-200 rounded-md bg-zinc-50/50">
        <button
          onClick={() => setHideSubmitted((v) => !v)}
          className={`inline-flex items-center gap-1 h-7 px-2 text-[10px] font-mono uppercase tracking-wider rounded border ${
            hideSubmitted ? "bg-blue-50 border-blue-200 text-blue-800" : "bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50"
          }`}
          data-testid={`reminder-manual-${kind}-filter-hide-submitted`}
        >
          <Filter size={11} /> Masquer ceux qui ont soumis
        </button>
        <div className="flex items-center gap-1 text-[11px] text-zinc-500 border-l border-zinc-200 pl-3">
          Canaux :
          <ChannelChip active={channels.includes("inapp")} icon={BellRing} label="In-app"
                       onClick={() => toggleChannel("inapp")} testid={`reminder-manual-${kind}-channel-inapp`} />
          <ChannelChip active={channels.includes("email")} icon={Mail} label="Email"
                       onClick={() => toggleChannel("email")} testid={`reminder-manual-${kind}-channel-email`} />
        </div>
        <div className="ml-auto">
          <button
            onClick={send}
            disabled={sending || selected.size === 0 || channels.length === 0}
            data-testid={`reminder-manual-${kind}-send`}
            className="inline-flex items-center gap-1.5 h-8 px-3 bg-zinc-900 hover:bg-zinc-800 text-white text-[11px] font-mono uppercase tracking-wider rounded-md disabled:opacity-50"
          >
            {sending ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
            Relancer {selected.size > 0 ? `${selected.size} collaborateur(s)` : "(0)"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-xs text-zinc-500 flex items-center gap-1.5 py-4">
          <Loader2 size={12} className="animate-spin" /> Chargement…
        </div>
      ) : visibleUsers.length === 0 ? (
        <div className="p-4 border border-zinc-200 rounded bg-zinc-50/40 text-xs text-zinc-600 text-center"
             data-testid={`reminder-manual-${kind}-empty`}>
          Aucun collaborateur à afficher.
        </div>
      ) : (
        <ul className="divide-y divide-zinc-100 border border-zinc-200 rounded-md overflow-hidden" data-testid={`reminder-manual-${kind}-list`}>
          <li className="flex items-center gap-2 px-3 py-1.5 bg-zinc-50 text-[10px] font-mono uppercase tracking-wider text-zinc-500">
            <button
              type="button" onClick={toggleAll}
              data-testid={`reminder-manual-${kind}-toggle-all`}
              className="inline-flex items-center gap-1 hover:text-zinc-900"
            >
              {allVisibleSelected ? <CheckSquare size={13} /> : <Square size={13} />}
              {allVisibleSelected ? "Tout désélectionner" : `Tout sélectionner (${visibleUsers.length})`}
            </button>
            <span className="ml-auto">{selected.size} sélectionné(s)</span>
          </li>
          {visibleUsers.map((u) => (
            <UserRow
              key={u.user_id}
              kind={kind}
              user={u}
              checked={selected.has(u.user_id)}
              onToggle={() => toggleOne(u.user_id)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function UserRow({ kind, user, checked, onToggle }) {
  const status = kind === "weekly" ? user.weekly : user.monthly;
  const submitted = !!status?.submitted;
  const lastManualAt = status?.last_manual_at;
  const testid = `reminder-manual-row-${user.user_id}`;
  return (
    <li
      className={`flex flex-wrap items-center gap-3 px-3 py-2 ${submitted ? "bg-emerald-50/30" : ""} ${checked ? "bg-blue-50/40" : ""}`}
      data-testid={testid}
      onClick={onToggle}
      style={{ cursor: "pointer" }}
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        data-testid={`${testid}-check`}
        className="shrink-0"
      >
        {checked ? <CheckSquare size={14} className="text-blue-700" /> : <Square size={14} className="text-zinc-400" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-zinc-900 truncate">{user.user_name}</span>
          <span className="text-[10px] font-mono text-zinc-400">{user.role}</span>
          {submitted ? (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider rounded bg-emerald-50 text-emerald-700 border border-emerald-100" data-testid={`${testid}-submitted`}>
              <CheckCircle2 size={9} /> Soumis
            </span>
          ) : (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider rounded bg-amber-50 text-amber-700 border border-amber-100">
              <AlertCircle size={9} /> En attente
            </span>
          )}
        </div>
        <div className="text-[11px] text-zinc-500 truncate">{user.email || "—"}</div>
        {lastManualAt && (
          <div className="text-[10px] text-zinc-400 mt-0.5" data-testid={`${testid}-last-fire`}>
            Dernière relance manuelle : {(lastManualAt || "").slice(0, 16).replace("T", " ")}
            {status?.last_manual_channels && ` · ${status.last_manual_channels.join("+")}`}
          </div>
        )}
      </div>
    </li>
  );
}

function ChannelChip({ active, icon: Icon, label, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1 h-7 px-2 text-[10px] font-mono uppercase tracking-wider rounded-md border ${
        active
          ? "bg-blue-50 border-blue-200 text-blue-800"
          : "bg-white border-zinc-200 text-zinc-500 hover:bg-zinc-50"
      }`}
    >
      <Icon size={11} /> {label}
    </button>
  );
}
